/**
 * One proposal-wide choose-one set: every structure with the exclusive
 * flag on is mutually exclusive. Independent of `clientOptional`.
 */

import type { ProposalNodeSet } from '../types';

type ExclusiveFlags = Pick<ProposalNodeSet, 'clientExclusive' | 'clientExclusiveGroup'>;

/** True when this structure is in the choose-one set. */
export function structureIsExclusive(ns: ExclusiveFlags): boolean {
  if (ns.clientExclusive === true) return true;
  return !!(ns.clientExclusiveGroup || '').trim();
}

/** @deprecated Use structureIsExclusive */
export const structureHasExclusiveGroup = structureIsExclusive;

/** Client may turn this structure on/off (optional add-on or exclusive member). */
export function isStructureClientSelectable(
  ns: Pick<ProposalNodeSet, 'clientOptional'> & ExclusiveFlags,
): boolean {
  return ns.clientOptional === true || structureIsExclusive(ns);
}

/** Enabled and not an independent optional — exclusive members still count. */
export function isStructureRequiredCoverage(
  ns: Pick<ProposalNodeSet, 'enabled' | 'clientOptional'>,
): boolean {
  return ns.enabled !== false && ns.clientOptional !== true;
}

export function canMarkClientOptional(
  nodeSets: Array<Pick<ProposalNodeSet, 'id' | 'enabled' | 'clientOptional'>>,
  nodeSetId: string,
): boolean {
  const target = nodeSets.find(ns => ns.id === nodeSetId);
  if (!target) return false;
  if (target.clientOptional === true) return true;
  return nodeSets.some(
    ns => ns.id !== nodeSetId && isStructureRequiredCoverage(ns),
  );
}

export function applyClientOptionalToNodeSet<T extends Pick<ProposalNodeSet, 'clientOptional'> & ExclusiveFlags>(
  ns: T,
  optional: boolean,
): T {
  if (optional) {
    return {
      ...ns,
      clientOptional: true,
      clientExclusive: false,
      clientExclusiveGroup: undefined,
    };
  }
  return { ...ns, clientOptional: false };
}

export function applyClientExclusiveToNodeSet<T extends Pick<ProposalNodeSet, 'clientOptional'> & ExclusiveFlags>(
  ns: T,
  exclusive: boolean,
): T {
  if (exclusive) {
    return {
      ...ns,
      clientExclusive: true,
      clientOptional: false,
      clientExclusiveGroup: undefined,
    };
  }
  return { ...ns, clientExclusive: false, clientExclusiveGroup: undefined };
}

export function listExclusiveStructures<T extends ProposalNodeSet>(nodeSets: T[]): T[] {
  return nodeSets.filter(ns => ns.enabled !== false && structureIsExclusive(ns));
}

/**
 * Exactly one selected exclusive structure among those not already excluded.
 * If every exclusive member is already false (e.g. phase-off), leave them false.
 */
export function normalizeExclusiveGroupSelections(
  nodeSets: ProposalNodeSet[],
  selections: Record<string, boolean>,
): Record<string, boolean> {
  const next = { ...selections };
  const members = listExclusiveStructures(nodeSets);
  if (members.length === 0) return next;

  const included = members.filter(ns => next[ns.id] !== false);
  if (included.length === 0) {
    const anyKey = members.some(ns => Object.prototype.hasOwnProperty.call(next, ns.id));
    if (!anyKey) next[members[0].id] = true;
    return next;
  }
  const keepId = included[0].id;
  for (const ns of members) {
    next[ns.id] = ns.id === keepId;
  }
  return next;
}

/** Turn on `structureId` and turn off every other exclusive structure. */
export function selectExclusiveGroupMember(
  nodeSets: ProposalNodeSet[],
  selections: Record<string, boolean>,
  structureId: string,
): Record<string, boolean> | null {
  const target = nodeSets.find(ns => ns.id === structureId);
  if (!target || target.enabled === false || !structureIsExclusive(target)) {
    return null;
  }
  const next = { ...selections };
  for (const ns of listExclusiveStructures(nodeSets)) {
    next[ns.id] = ns.id === structureId;
  }
  return next;
}
